import type { PropsWithChildren } from 'react';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Wheel } from '@sorare/core/src/atoms/icons/Wheel';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { NotIOsAppFeature } from '@sorare/core/src/components/BuyableFeature';
import { ANY_SPORT_MARKET_WHEEL } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useEnabledWheelByRarity } from '@sorare/core/src/hooks/useEnabledWheelByRarity';
import { useWheelTicketDistributionEndedByRarity } from '@sorare/core/src/hooks/useWheelTicketDistributionEndedByRarity';

import { useWheelTickets } from '#src/hooks/wheel/useWheelTickets.tsx';

export const NavigationSorareWheel = ({ children }: PropsWithChildren) => {
  const enabledWheelByRarity = useEnabledWheelByRarity();
  const distributionEndedByRarity = useWheelTicketDistributionEndedByRarity();
  const { ticketCount, canClaimTicket } = useWheelTickets();
  const { generateSportContextPath } = useSportContext();
  const isUsingSeries = useSeriesNav();
  const invisible = ticketCount === 0 && !canClaimTicket;
  const variant = (canClaimTicket && ticketCount === 0 && 'dot') || undefined;
  const badgeContent = ticketCount !== 0 && ticketCount;

  const relevantRarities = isUsingSeries
    ? (['common'] as const)
    : (Object.keys(
        enabledWheelByRarity
      ) as (keyof typeof enabledWheelByRarity)[]);

  if (!relevantRarities.some(rarity => enabledWheelByRarity[rarity])) {
    return null;
  }

  const allEnabledWheelsEnded = relevantRarities.every(
    rarity => !enabledWheelByRarity[rarity] || distributionEndedByRarity[rarity]
  );
  if (allEnabledWheelsEnded && ticketCount === 0) {
    return null;
  }

  return (
    <NotIOsAppFeature>
      <ButtonBase to={generateSportContextPath(ANY_SPORT_MARKET_WHEEL)}>
        <Horizontal>
          <Badge
            badgeContent={badgeContent}
            variant={variant}
            overlap="circular"
            color="red"
            size="medium"
            invisible={invisible}
          >
            <IconButton size="small" color="transparent" asSpan>
              <Wheel />
            </IconButton>
          </Badge>
          {children}
        </Horizontal>
      </ButtonBase>
    </NotIOsAppFeature>
  );
};
